// =====================================================
// TYPES: PERMISSIONS
// Description: Types TypeScript pour le système de permissions
// =====================================================

import type { UserRole } from './user';

/**
 * Entités du système
 */
export type EntityType =
  | 'contact'
  | 'crew'
  | 'product'
  | 'vehicle'
  | 'school'
  | 'association'
  | 'discount'
  | 'form' // Formulaires (templates)
  | 'prospect' // Prospects (form_submissions - soumissions de formulaires)
  | 'timeline'
  | 'task'
  | 'user';

/**
 * Actions possibles sur les entités
 */
export type Action =
  | 'view'
  | 'view_all'
  | 'create'
  | 'update'
  | 'delete'
  | 'import'
  | 'export'
  | 'delete_bulk'
  | 'manage_users';

/**
 * Permissions pour une entité donnée
 */
export interface EntityPermissions {
  view: boolean; // Peut voir l'entité
  viewAll: boolean; // Peut voir toutes les données (sinon uniquement les siennes)
  create: boolean; // Peut créer
  update: boolean; // Peut modifier
  delete: boolean; // Peut supprimer
  import: boolean; // Peut importer
  export: boolean; // Peut exporter
  deleteBulk: boolean; // Peut supprimer en masse
}

/**
 * Ensemble de toutes les permissions utilisateur
 */
export interface UserPermissions {
  role: UserRole;

  // Permissions générales
  canManageUsers: boolean;
  canViewDashboard: boolean;
  canAccessAllData: boolean;

  // Permissions par entité
  contacts: EntityPermissions;
  crews: EntityPermissions;
  products: EntityPermissions;
  vehicles: EntityPermissions;
  schools: EntityPermissions;
  associations: EntityPermissions;
  discounts: EntityPermissions;
  forms: EntityPermissions; // Formulaires (templates)
  prospects: EntityPermissions; // Prospects (form_submissions)
  timeline: EntityPermissions;
  tasks: EntityPermissions;
}

/**
 * Configuration des permissions par rôle et par entité
 * Basé sur le tableau "Gestion des droits CRM 4L"
 */
export const PERMISSIONS_CONFIG: Record<UserRole, Omit<UserPermissions, 'role'>> = {
  // Admin (Floran) : Tous les droits
  superadmin: {
    canManageUsers: true,
    canViewDashboard: true,
    canAccessAllData: true,

    contacts: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    crews: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    products: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    vehicles: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    schools: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    associations: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    discounts: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    forms: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    prospects: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    timeline: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: false,
      export: true,
      deleteBulk: true,
    },
    tasks: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: false,
      export: true,
      deleteBulk: true,
    },
  },

  // Désertours + Présidents 4L : Droits étendus
  admin: {
    canManageUsers: false,
    canViewDashboard: true,
    canAccessAllData: true,

    contacts: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    crews: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    products: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    vehicles: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    schools: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    associations: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    discounts: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    forms: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    prospects: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: true,
      export: true,
      deleteBulk: true,
    },
    timeline: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: false,
      export: true,
      deleteBulk: true,
    },
    tasks: {
      view: true,
      viewAll: true,
      create: true,
      update: true,
      delete: true,
      import: false,
      export: true,
      deleteBulk: true,
    },
  },

  // Asso 4L : Accès à toute la data en lecture, actions limitées
  user: {
    canManageUsers: false,
    canViewDashboard: true,
    canAccessAllData: true, // Peut voir toute la data

    contacts: {
      view: true,
      viewAll: true, // Accès à tous les contacts
      create: true, // Peut créer des contacts
      update: true, // Peut modifier les contacts
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    crews: {
      view: true,
      viewAll: true, // Accès à tous les équipages
      create: true,
      update: true,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    products: {
      view: true,
      viewAll: true, // Accès à tous les produits
      create: false,
      update: false,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    vehicles: {
      view: true,
      viewAll: true, // Accès à tous les véhicules
      create: true,
      update: true,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    schools: {
      view: true,
      viewAll: true, // Accès à toutes les écoles
      create: true,
      update: true,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    associations: {
      view: true,
      viewAll: true, // Accès à toutes les associations
      create: false,
      update: false,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    discounts: {
      view: true,
      viewAll: true, // Accès à toutes les réductions
      create: false,
      update: false,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    forms: {
      view: true,
      viewAll: true, // Accès à tous les formulaires
      create: false,
      update: false,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    prospects: {
      view: true,
      viewAll: true, // Accès à tous les prospects
      create: false, // Ne peut pas créer manuellement
      update: false,
      delete: true, // Sprint 4.1 : Permettre la suppression individuelle de prospects
      import: false,
      export: false,
      deleteBulk: false,
    },
    timeline: {
      view: true,
      viewAll: true, // Accès à toute la timeline
      create: true, // Peut enregistrer ses appels
      update: false,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
    tasks: {
      view: true,
      viewAll: true, // Accès à toutes les tâches
      create: true,
      update: true,
      delete: false,
      import: false,
      export: false,
      deleteBulk: false,
    },
  },
};
